//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { IInstagramUsedHashtag } from '@apis/instagram-hashtags-usage/instagramHashtagsUsageApiModels';
import { IConnectedBrandInstagramBusinessAccounts } from '@apis/instagram-settings/gatewayInstagramSettingsApiModel';
import { ProcessedHashtagUsageResponse } from '@hashtags/processedHashtagUsageResponseModel';
import { Pagination } from '@tracked-sources/Pagination';

export enum FilterState {
  ALL = 'all',
  TRACKING = 'tracking',
  UNTRACKED = 'untracked',
  USED_ELSEWHERE = 'usedElsewhere',
}

export const FilterLabels = {
  [FilterState.ALL]: 'All Hashtags',
  [FilterState.TRACKING]: 'Tracking in Vibe',
  [FilterState.UNTRACKED]: 'Untracked',
  [FilterState.USED_ELSEWHERE]: 'Used elsewhere',
} as const;

const TableHeaderLabels = {
  Hashtag: 'Hashtag',
  HashtagDetails: 'Hashtag Details',
  TrackedDate: 'Tracked Date',
  IgAccount: 'IG Account',
  Action: 'Action',
} as const;

export interface HashtagsTableContainerProps {
  multiHandleHashtagsUsage: ProcessedHashtagUsageResponse | undefined;
  connectedAccounts: IConnectedBrandInstagramBusinessAccounts[] | undefined;
}

export interface HashtagsTableProps {
  connectedAccounts: IConnectedBrandInstagramBusinessAccounts[] | undefined;
  filteredHashtags: HashtagRowData[];
  pagination: Pagination;
  setPagination: (newValue: Pagination) => void;
}

export interface HashtagRowData extends IInstagramUsedHashtag {
  descriptionText: string;
}

export interface HashtagsRowProps {
  hashtag: HashtagRowData;
  connectedAccounts: IConnectedBrandInstagramBusinessAccounts[] | undefined;
}

export interface TableHeader {
  label: string;
}

export const TABLE_HEADERS: TableHeader[] = [
  { label: TableHeaderLabels.Hashtag },
  { label: TableHeaderLabels.HashtagDetails },
  { label: TableHeaderLabels.TrackedDate },
  { label: TableHeaderLabels.IgAccount },
  { label: TableHeaderLabels.Action },
] as const;

export const HASHTAG_FILTERS: Record<
  string,
  (hashtag: HashtagRowData) => boolean
> = {
  [FilterState.ALL]: () => true,
  [FilterState.TRACKING]: (hashtag) => hashtag.isTracked,
  [FilterState.UNTRACKED]: (hashtag) =>
    !hashtag.isTracked &&
    hashtag.descriptionText.includes(FilterLabels[FilterState.UNTRACKED]),
  [FilterState.USED_ELSEWHERE]: (hashtag) =>
    !hashtag.isTracked &&
    hashtag.descriptionText.includes(FilterLabels[FilterState.USED_ELSEWHERE]),
};
